import React, { ChangeEvent, useState, useEffect } from "react";
import {
  ButtonForm,
  CheckboxContainer,
  DropdownWrapper,
  Form,
  StyledLabel,
  SubtitleForm,
  TermsButton,
  VisuallyHiddenLabel,
  Wrapper,
} from "./loginForm.styles";
import TextField from "@flame-ui/core/textField";
import Checkbox from "@flame-ui/core/checkbox";
import ModalInfo from "../Modal/Modal";
import WarningAlert from "../WarningAlert";
import { getEventTag, getViewTag } from "../../utils/getGTMtag";
import { formatCUIT, validateField } from "../../utils/validationUtils";
import Dropdown from "@flame-ui/core/dropdown";
import Option from "@flame-ui/core/option";

export interface DatosForm {
  email: string;
  cuit: string;
  destino: string;
  numero_ubicacion?: string;
}

interface ErrorFormulario {
  email?: string;
  cuit?: string;
  numeroMesaSala?: string;
}

interface FormularioProps {
  titulo: string;
  onSubmit: (datosForm: DatosForm) => void;
}

const LoginForm: React.FC<FormularioProps> = ({ titulo, onSubmit }) => {
  const [email, setEmail] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [cuit, setCuit] = useState("");
  const [value, setValue] = useState<string>("");
  const [numeroMesaSala, setNumeroMesaSala] = useState<string>("");
  const [alertTermsAccepted, setAlertTermsAccepted] = useState(false);
  const [errors, setErrors] = useState<ErrorFormulario>({
    cuit: "",
    email: "",
    numeroMesaSala: "",
  });

  const requiereNumeroMesaSala = value === "Mesa-Coworking" || value === "Sala";

  const [firstTag, setFirstTag] = useState(false);
  useEffect(() => setFirstTag(true), []);
  useEffect(() => {
    if (firstTag) {
      getViewTag({
        screen: "home_workcafe",
        signal: "screen_view",
      });
    }
  }, [firstTag]);

  const handleInputChange = (field: "email" | "cuit", value: string) => {
    if (field === "email") {
      setEmail(value);
    }
    if (field === "cuit") {
      const formattedValue = formatCUIT(value);
      setCuit(formattedValue);
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: validateField(field, value),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const emailError = validateField("email", email);
    const cuitError = validateField("cuit", cuit);

    const numeroMesaSalaError = requiereNumeroMesaSala
      ? numeroMesaSala.trim() === ""
        ? value === "Sala"
          ? "Ingresá el número de Sala"
          : "Ingresá el número de Mesa"
        : ""
      : "";
    setErrors({
      email: emailError,
      cuit: cuitError,
      numeroMesaSala: numeroMesaSalaError,
    });

    getEventTag({
      signal: "pedido_internet",
      tipo_signal: "interaccion",
    });

    const sinErrores = !emailError && !cuitError && !numeroMesaSalaError;

    if (sinErrores && termsAccepted) {
      onSubmit({
        email,
        cuit,
        destino: value,
        numero_ubicacion: requiereNumeroMesaSala ? numeroMesaSala : undefined,
      });
      setAlertTermsAccepted(false);
    } else if (sinErrores && !termsAccepted) {
      setAlertTermsAccepted(true);
    }
  };

  return (
    <>
      <Wrapper>
        <SubtitleForm>Ingrese sus datos y acceda al Wifi</SubtitleForm>
        <Form onSubmit={handleSubmit} noValidate>
          <VisuallyHiddenLabel htmlFor="cuit">DNI o CUIT</VisuallyHiddenLabel>
          <TextField
            id="cuit"
            placeholder="Ingresá tu DNI o CUIT"
            value={cuit}
            error={errors.cuit}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleInputChange("cuit", e.target.value)
            }
            aria-labelledby={errors.cuit ? "cuitError" : ""}
          />
          <VisuallyHiddenLabel htmlFor="email">Email</VisuallyHiddenLabel>
          <TextField
            id="email"
            placeholder="tu@email.com"
            data-testid="email"
            value={email}
            error={errors.email}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleInputChange("email", e.target.value)
            }
            aria-labelledby={errors.email ? "emailError" : ""}
          />
          <DropdownWrapper>
            <Dropdown label="Sector" value={value}>
              <Option
                value="Mesa-Coworking"
                onClick={() => setValue("Mesa-Coworking")}
              >
                Mesa Coworking
              </Option>
              <Option value="Sala" onClick={() => setValue("Sala")}>
                Sala
              </Option>
              <Option
                value="Take-Away"
                onClick={() => {
                  setValue("Take-Away");
                  setNumeroMesaSala("");
                  setErrors((prev) => ({ ...prev, numeroMesaSala: "" }));
                }}
              >
                Take Away
              </Option>
            </Dropdown>
          </DropdownWrapper>
          {requiereNumeroMesaSala && (
            <>
              <VisuallyHiddenLabel htmlFor="numeroMesaSala">
                Número de Mesa/Sala
              </VisuallyHiddenLabel>
              <TextField
                id="numeroMesaSala"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={
                  value === "Sala"
                    ? "Ingresá el número de Sala"
                    : "Ingresá el número de Mesa"
                }
                value={numeroMesaSala}
                error={errors.numeroMesaSala}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setNumeroMesaSala(val);
                  setErrors((prev) => ({
                    ...prev,
                    numeroMesaSala:
                      val.trim() === ""
                        ? value === "Sala"
                          ? "Ingresá el número de Sala"
                          : "Ingresá el número de Mesa"
                        : "",
                  }));
                }}
                aria-labelledby={
                  errors.numeroMesaSala ? "numeroMesaSalaError" : ""
                }
              />
            </>
          )}
          <CheckboxContainer>
            <Checkbox
              id="pepe"
              data-testid="pepe"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <StyledLabel htmlFor="termsCheckbox">
              {" "}
              Al ingresar, estás aceptando los
              <TermsButton
                onClick={(e) => {
                  e.preventDefault();
                  setShowModal(true);
                }}
              >
                {" "}
                Términos y condiciones.
              </TermsButton>
            </StyledLabel>
          </CheckboxContainer>
          {alertTermsAccepted && <WarningAlert />}
          <ButtonForm type="submit" size="small" variant="primary">
            Iniciar sesión
          </ButtonForm>
        </Form>
      </Wrapper>
      {showModal && (
        <ModalInfo
          onClose={() => setShowModal(false)}
          content="El titular de los datos personales presta su consentimiento libre, expreso e informado para que los datos provistos sean utilizados y tratados en la base de datos de Banco Santander  S.A. con domicilio sito en la calle Av. Juan de Garay 151 C.A.B.A. Los mismos son recabados con la finalidad de ofrecer y/o publicitar bienes, productos y/o servicios que son objeto de la actividad que desarrolla. Las respuestas incluidas en el presente formulario revisten el carácter de facultativas para quien lo suscribe. Se informa que el interesado podrá en cualquier momento ejercer los derechos de acceso, rectificación y supresión de sus datos. La AGENCIA DE ACCESO A LA INFORMACIÓN PÚBLICA, Órgano de Control de la Ley Nº 25.326, atiende las denuncias y reclamos que se interpongan con relación al incumplimiento de las normas sobre protección de datos personales."
        />
      )}
    </>
  );
};

export default LoginForm;
